export const SITE_NAME = "HALTONS.CA";
export const SITE_URL = "https://haltons.ca";
export const TAGLINE = "Your Region. Your Candidates.";
export const EDITORIAL_THEME = "The People Shaping Halton";
export const ELECTION_DATE = "Monday, October 26, 2026";
export const LAST_UPDATED = "October 2026";

const PRIORITIES_NOTE =
    "The following are stated campaign commitments made by the candidate. They are presented as campaign promises, not as completed municipal achievements.";

const toTitles = (titles) => titles.map((title) => ({ title }));

export const municipalities = [
    {
        slug: "milton",
        name: "Milton",
        designation: "Town of Milton",
        officialUrl: "https://www.milton.ca",
        officialLabel: "Town of Milton — official website",
        mark: "escarpment",
        blurb: "Mayoral candidate directory and election coverage for the Town of Milton.",
    },
    {
        slug: "oakville",
        name: "Oakville",
        designation: "Town of Oakville",
        officialUrl: "https://www.oakville.ca",
        officialLabel: "Town of Oakville — official website",
        mark: "harbour",
        blurb: "Mayoral candidate directory and election coverage for the Town of Oakville.",
    },
    {
        slug: "burlington",
        name: "Burlington",
        designation: "City of Burlington",
        officialUrl: "https://www.burlington.ca",
        officialLabel: "City of Burlington — official website",
        mark: "bridge",
        blurb: "Mayoral candidate directory and election coverage for the City of Burlington.",
    },
    {
        slug: "halton-hills",
        name: "Halton Hills",
        designation: "Town of Halton Hills",
        officialUrl: "https://www.haltonhills.ca",
        officialLabel: "Town of Halton Hills — official website",
        mark: "hills",
        blurb: "Mayoral candidate directory and election coverage for the Town of Halton Hills.",
    },
];

export const candidates = [
    {
        slug: "zainab-azim",
        name: "Zainab Azim",
        municipality: "milton",
        office: "Mayor of Milton",
        detailed: true,
        featured: true,
        series: "Emerging Leaders of Halton",
        editorialTitle: "A New Generation of Civic Leadership.",
        lede: "Zainab Azim is a Milton community organizer and candidate for Mayor of Milton in the 2026 municipal election. Her background spans public policy, education and community organizing — including experience in the Office of Canada's Deputy Prime Minister and Minister of Finance, a Harvard Master of Education, and recognition as Milton's 2022 Citizen of the Year.",
        achievements: [
            {
                title: "Milton's 2022 Citizen of the Year",
                text: "Recognized by the Town of Milton for her contributions to the community.",
            },
            {
                title: "Harvard Master of Education",
                text: "Graduate of Harvard University, with academic and teaching-fellow experience.",
            },
            {
                title: "Youngest Official Bellringer",
                text: "Reported as the youngest person to hold the distinction of Official Bellringer.",
            },
            {
                title: "Space4Women Mentor",
                text: "Mentors through the United Nations Office for Outer Space Affairs' Space4Women initiative.",
            },
            {
                title: "Federal public-policy experience",
                text: "Reported work in the Office of Canada's Deputy Prime Minister and Minister of Finance.",
            },
        ],
        sections: [
            {
                heading: "Education and academic experience",
                paragraphs: [
                    "Azim holds a Master of Education from Harvard University, where she gained academic and teaching-fellow experience. Her academic work has involved policy, economics and community organizing — a combination that connects classroom study with the practical questions of how communities make decisions.",
                ],
            },
            {
                heading: "Political organizing",
                paragraphs: [
                    "Azim was involved in Zohran Mamdani's mayoral campaign in New York, where she is reported to have gained organizing and research experience. That experience is relevant to her understanding of political engagement and community mobilization — how campaigns listen to residents, and how they turn concerns into organized participation.",
                ],
            },
            {
                heading: "Community recognition",
                paragraphs: [
                    "In 2022, Azim was named Milton's Citizen of the Year. She is also reported to be the youngest Official Bellringer, and she mentors through the United Nations Office for Outer Space Affairs' Space4Women initiative, which supports women and girls pursuing opportunities in the space sector.",
                ],
            },
            {
                heading: "Professional experience",
                paragraphs: [
                    "Azim is reported to have worked in the Office of Canada's Deputy Prime Minister and Minister of Finance. Across public policy, organizing, education and community engagement, her experience spans the federal, academic and grassroots levels of public life.",
                ],
            },
            {
                heading: "Campaign and public engagement",
                paragraphs: [
                    "Azim's campaign has included public endorsements, campaign events and media interviews. Her approach emphasizes direct engagement with residents and communicating her campaign in public, in person and through the coverage her candidacy has received.",
                ],
            },
            {
                heading: "Endorsements and public support",
                paragraphs: [
                    "Azim's campaign has attracted support from an accomplished group of public figures and community members, including Marshall Ganz, a Harvard professor and political organizer associated with Barack Obama's 2008 presidential campaign and Zohran Mamdani's mayoral campaign; Kathryn McGarry, former Mayor of Cambridge and former Ontario cabinet minister; sitting MPs Salma Zahid and Sima Acan; former MP Bryan May; and Richard Furlong, a longtime Milton lawyer. Milton residents Sedra and Ahmed Alshamalsy have also publicly endorsed her.",
                    "This range of supporters brings together experience in political organizing, public service and community life.",
                ],
            },
        ],
        timeline: [
            {
                period: "Harvard University",
                title: "Master of Education",
                text: "Graduate study with academic and teaching-fellow experience, involving policy, economics and community organizing.",
            },
            {
                period: "Community work",
                title: "Space4Women mentor",
                text: "Mentoring through the United Nations Office for Outer Space Affairs' Space4Women initiative.",
            },
            {
                period: "2022",
                title: "Milton Citizen of the Year",
                text: "Recognized as Milton's 2022 Citizen of the Year; reported as the youngest Official Bellringer.",
            },
            {
                period: "Federal public service",
                title: "Office of the Deputy Prime Minister and Minister of Finance",
                text: "Reported work in the Office of Canada's Deputy Prime Minister and Minister of Finance.",
            },
            {
                period: "Campaign organizing",
                title: "Zohran Mamdani mayoral campaign",
                text: "Organizing and research experience on Zohran Mamdani's mayoral campaign in New York.",
            },
            {
                period: "2026",
                title: "Candidate for Mayor of Milton",
                text: "Contesting the Milton mayoral election on October 26, 2026.",
            },
        ],
        priorities: {
            note: PRIORITIES_NOTE,
            items: [
                {
                    title: "Local career opportunities",
                    text: "Creating career opportunities within Milton so more residents can build their working lives in the community.",
                },
                {
                    title: "More frequent public transit",
                    text: "More frequent transit service, including a proposed 20-minute service frequency where stated in her platform.",
                },
                {
                    title: "Community life beyond working hours",
                    text: "Strengthening community life beyond conventional working hours, so Milton remains active past the commute.",
                },
            ],
        },
        sources: [],
        media: [],
        lastUpdated: LAST_UPDATED,
    },
    {
        slug: "gord-krantz",
        name: "Gord Krantz",
        municipality: "milton",
        office: "Mayor of Milton",
        detailed: true,
        featured: false,
        series: null,
        editorialTitle: "Experience and Continuity.",
        lede: "Gord Krantz has served as Milton's mayor since 1980, following his election to Town Council in 1965. A lifelong Milton resident, he is seeking re-election in 2026.",
        achievements: [],
        sections: [
            {
                heading: "Before and beyond public office",
                paragraphs: [
                    "A lifelong Milton resident, Krantz previously owned Krantz Fuels and served as a part-time firefighter.",
                ],
            },
            {
                heading: "Community projects",
                paragraphs: [
                    "During his decades in public service, Krantz has been involved in major community projects, including the Milton Sports Centre, the Milton Centre for the Arts and the Mill Pond restoration.",
                ],
            },
        ],
        timeline: [
            {
                period: "1965",
                title: "Elected to Town Council",
                text: "Elected to Milton Town Council.",
            },
            {
                period: "1980",
                title: "Mayor of Milton",
                text: "Became Milton's mayor in 1980 and has served in the office since.",
            },
            {
                period: "2026",
                title: "Seeking re-election",
                text: "Contesting the Milton mayoral election on October 26, 2026.",
            },
        ],
        priorities: {
            note: PRIORITIES_NOTE,
            items: [
                {
                    title: "Experience, stability and affordability",
                    text: "Emphasizes experience, stability and affordability in his re-election campaign.",
                },
                {
                    title: "Economic growth and downtown revitalization",
                    text: "Prioritizes local economic growth and the revitalization of downtown Milton.",
                },
                {
                    title: "Property taxes",
                    text: "Has pledged to oppose what he describes as excessive property tax increases.",
                },
            ],
        },
        sources: [],
        media: [],
        lastUpdated: LAST_UPDATED,
    },
    {
        slug: "sameera-ali",
        name: "Sameera Ali",
        municipality: "milton",
        office: "Mayor of Milton",
        detailed: true,
        featured: false,
        series: null,
        editorialTitle: "A Career Across Municipal and Provincial Politics.",
        lede: "Sameera Ali is a candidate for Mayor of Milton in the 2026 municipal election. She has served as both a town councillor and a regional councillor, has contested provincial elections, and is now seeking the mayor's office.",
        achievements: [],
        sections: [
            {
                heading: "Political and municipal history",
                paragraphs: [
                    "Ali has served as both a town councillor and a regional councillor, giving her experience at the two levels of municipal government that serve Milton. She has also contested provincial elections, extending her political activity beyond municipal office.",
                    "She is now running for mayor.",
                ],
            },
        ],
        timeline: [
            {
                period: "Municipal office",
                title: "Town councillor",
                text: "Served as a town councillor.",
            },
            {
                period: "Municipal office",
                title: "Regional councillor",
                text: "Served as a regional councillor.",
            },
            {
                period: "Provincial candidacy",
                title: "Provincial election candidate",
                text: "Contested provincial elections.",
            },
            {
                period: "2026",
                title: "Candidate for Mayor of Milton",
                text: "Contesting the Milton mayoral election on October 26, 2026.",
            },
        ],
        priorities: {
            note: PRIORITIES_NOTE,
            items: toTitles([
                "Affordability",
                "Community safety",
                "Responsible growth",
                "Improved transit",
                "Local jobs",
                "Protecting Milton's rural character",
            ]),
        },
        editorial: {
            heading: "Editorial note",
            text: "Ali's continued pursuit of elected office raises a fair question: whether she is seeking a new level of public service, or the next step in her political career. HALTONS.CA presents the documented chronology above and leaves that judgment to readers.",
        },
        sources: [],
        media: [],
        lastUpdated: LAST_UPDATED,
    },
    {
        slug: "nadeem-akbar",
        name: "Nadeem Akbar",
        municipality: "milton",
        office: "Mayor of Milton",
        detailed: true,
        featured: false,
        series: null,
        editorialTitle: "From Federal Politics to Municipal Leadership.",
        lede: "Nadeem Akbar is a candidate for Mayor of Milton in the 2026 municipal election. His candidacy follows a recent run in federal politics.",
        achievements: [],
        sections: [
            {
                heading: "Electoral history",
                paragraphs: [
                    "Akbar contested a recent federal election and lost. He subsequently did not obtain another federal nomination. He is now contesting the Milton mayoral election.",
                    "During his earlier federal campaign, Akbar introduced himself as a Burlington resident in a local news outlet.",
                ],
            },
        ],
        timeline: [
            {
                period: "Federal election",
                title: "Federal election candidate",
                text: "Contested a recent federal election and lost.",
            },
            {
                period: "Nomination",
                title: "No further federal nomination",
                text: "Did not obtain another federal nomination.",
            },
            {
                period: "2026",
                title: "Candidate for Mayor of Milton",
                text: "Contesting the Milton mayoral election on October 26, 2026.",
            },
        ],
        priorities: {
            note: PRIORITIES_NOTE,
            items: toTitles([
                "Responsible growth",
                "Affordability",
                "Better infrastructure and transit",
                "Public safety",
                "Transparent spending",
                "Municipal service improvements",
            ]),
        },
        sources: [],
        media: [],
        lastUpdated: LAST_UPDATED,
    },
    {
        slug: "maliha-ahmed",
        name: "Maliha K. Ahmed",
        municipality: "milton",
        office: "Mayor of Milton",
        detailed: true,
        featured: false,
        series: null,
        editorialTitle: "Social Work, Community Involvement and Municipal Politics.",
        lede: "Maliha K. Ahmed is a social worker and a candidate for Mayor of Milton in the 2026 municipal election.",
        achievements: [],
        sections: [
            {
                heading: "Professional background",
                paragraphs: [
                    "Ahmed is a social worker, a profession centred on direct work with individuals, families and communities.",
                ],
            },
            {
                heading: "Political participation",
                paragraphs: [
                    "Ahmed has worked with the NDP in the past. She is now contesting the Milton mayoral election.",
                ],
            },
            {
                heading: "Electoral history note",
                paragraphs: [
                    "Ahmed's husband also contested a mayoral election in the past and lost. This is noted as a separate piece of electoral history; it does not bear on her own qualifications or candidacy.",
                ],
            },
        ],
        timeline: [],
        priorities: {
            note: PRIORITIES_NOTE,
            items: toTitles([
                "Affordable transit",
                "Accessible childcare",
                "Sustainable housing",
                "Environmental protection",
                "Better support for families, seniors and students",
            ]),
        },
        sources: [],
        media: [],
        lastUpdated: LAST_UPDATED,
    },
    {
        slug: "george-minakakis",
        name: "George Minakakis",
        municipality: "milton",
        office: "Mayor of Milton",
        detailed: true,
        featured: false,
        series: null,
        editorialTitle: "Economic Growth and a New Direction.",
        lede: "George Minakakis is Milton's Ward 1 town councillor, elected in a 2025 byelection, and is now running for mayor in the 2026 municipal election.",
        achievements: [],
        sections: [
            {
                heading: "On growth and municipal finance",
                paragraphs: [
                    "Minakakis argues that residential growth alone cannot adequately fund the services the town needs. He advocates attracting more businesses, creating high-skilled local jobs and developing new revenue sources.",
                    "His campaign frames these priorities as essential to supporting Milton's future growth while managing pressure on property taxes and municipal services.",
                ],
            },
        ],
        timeline: [
            {
                period: "2025",
                title: "Ward 1 town councillor",
                text: "Elected Milton's Ward 1 town councillor in a 2025 byelection.",
            },
            {
                period: "2026",
                title: "Candidate for Mayor of Milton",
                text: "Contesting the Milton mayoral election on October 26, 2026.",
            },
        ],
        priorities: {
            note: PRIORITIES_NOTE,
            items: [
                {
                    title: "A new approach to growth",
                    text: "Calls for a new approach to managing Milton's rapid growth.",
                },
                {
                    title: "Fiscal responsibility and affordability",
                    text: "Emphasizes fiscal responsibility, affordability, economic development and sustainable municipal finances.",
                },
                {
                    title: "Jobs and new revenue",
                    text: "Advocates attracting more businesses, creating high-skilled local jobs and developing new revenue sources for the town.",
                },
            ],
        },
        sources: [],
        media: [],
        lastUpdated: LAST_UPDATED,
    },
    {
        slug: "julia-hanna",
        name: "Julia Hanna",
        municipality: "oakville",
        office: "Mayor of Oakville",
        detailed: true,
        featured: false,
        series: null,
        editorialTitle: null,
        lede: "Julia Hanna is an Oakville entrepreneur and community leader with decades of local experience, and a candidate for Mayor of Oakville in the 2026 municipal election.",
        achievements: [],
        sections: [
            {
                heading: "Business and community background",
                paragraphs: [
                    "Hanna founded several successful restaurant businesses and launched HealthyFam. She has chaired the Oakville Chamber of Commerce, bringing decades of local business and community experience to her mayoral campaign.",
                ],
            },
        ],
        timeline: [],
        priorities: {
            note: PRIORITIES_NOTE,
            items: [
                {
                    title: "Infrastructure before growth",
                    text: "Prioritizes having infrastructure in place before further growth.",
                },
                {
                    title: "Traffic reduction",
                    text: "Focuses on reducing traffic across Oakville.",
                },
                {
                    title: "Responsible spending",
                    text: "Commits to responsible spending at Town Hall.",
                },
                {
                    title: "Attainable housing",
                    text: "Supports attainable housing for Oakville residents.",
                },
                {
                    title: "Public safety",
                    text: "Makes public safety a priority of her platform.",
                },
                {
                    title: "Transparency and accountability",
                    text: "Calls for greater transparency and accountability at Town Hall.",
                },
            ],
        },
        sources: [],
        media: [],
        lastUpdated: LAST_UPDATED,
    },
    {
        slug: "rory-nisan",
        name: "Rory Nisan",
        municipality: "burlington",
        office: "Mayor of Burlington",
        detailed: true,
        featured: false,
        series: null,
        editorialTitle: null,
        lede: "Rory Nisan is a Burlington native who has served as Ward 3 councillor since 2018, and is a candidate for Mayor of Burlington in the 2026 municipal election.",
        achievements: [],
        sections: [
            {
                heading: "Before municipal politics",
                paragraphs: [
                    "Before entering municipal politics, Nisan worked as a Canadian foreign service officer, negotiating human-rights resolutions at the United Nations and leading strategic communications at Canada's NATO delegation.",
                ],
            },
            {
                heading: "Community and sector leadership",
                paragraphs: [
                    "Nisan chairs the Ontario Caucus of the Federation of Canadian Municipalities and the Conservation Halton Foundation.",
                ],
            },
        ],
        timeline: [
            {
                period: "2018",
                title: "Ward 3 councillor",
                text: "Elected Burlington's Ward 3 councillor in 2018; has served in the role since.",
            },
            {
                period: "2026",
                title: "Candidate for Mayor of Burlington",
                text: "Contesting the Burlington mayoral election on October 26, 2026.",
            },
        ],
        priorities: {
            note: PRIORITIES_NOTE,
            items: [
                {
                    title: "Fiscal responsibility and affordability",
                    text: "Focuses on fiscal responsibility, affordability and quality of life, including a proposed spending audit and limiting property-tax increases to inflation.",
                },
                {
                    title: "Local jobs",
                    text: "Supports local jobs in Burlington.",
                },
                {
                    title: "Public safety and value for residents",
                    text: "Strengthening public safety and ensuring municipal spending delivers value for residents.",
                },
            ],
        },
        sources: [
            {
                title: "Official campaign website — rorynisan.ca",
                url: "https://rorynisan.ca",
            },
        ],
        media: [],
        lastUpdated: LAST_UPDATED,
    },
    {
        slug: "jason-brass",
        name: "Jason Brass",
        municipality: "halton-hills",
        office: "Mayor of Halton Hills",
        detailed: true,
        featured: false,
        series: null,
        editorialTitle: null,
        lede: "Jason Brass is a lifelong Halton Hills resident, business leader and Ward 2 councillor, elected in 2022, and a candidate for Mayor of Halton Hills in the 2026 municipal election.",
        achievements: [],
        sections: [
            {
                heading: "Business leadership",
                paragraphs: [
                    "As CEO of Terra Cotta Foods Ltd., Brass has led business growth, team development and operational modernization.",
                ],
            },
            {
                heading: "Community involvement",
                paragraphs: [
                    "Before entering municipal politics, Brass served on community boards, including the Halton Hills Chamber of Commerce.",
                ],
            },
        ],
        timeline: [
            {
                period: "2022",
                title: "Ward 2 councillor",
                text: "Elected Halton Hills' Ward 2 councillor in 2022.",
            },
            {
                period: "2026",
                title: "Candidate for Mayor of Halton Hills",
                text: "Contesting the Halton Hills mayoral election on October 26, 2026.",
            },
        ],
        priorities: {
            note: PRIORITIES_NOTE,
            items: [
                {
                    title: "Responsible growth and infrastructure",
                    text: "Emphasizes responsible growth and infrastructure planning, ensuring infrastructure keeps pace with development.",
                },
                {
                    title: "Fiscal accountability",
                    text: "Commits to fiscal accountability and strengthening transparency at Town Hall.",
                },
                {
                    title: "Community safety and traffic",
                    text: "Supports community safety and improving traffic safety.",
                },
                {
                    title: "Rural character, farmland and heritage",
                    text: "Supports preserving Halton Hills' rural character and protecting farmland and heritage.",
                },
                {
                    title: "Recreational facilities",
                    text: "Supports expanding recreational facilities.",
                },
            ],
        },
        sources: [
            {
                title: "Official campaign website — jasonbrass.ca",
                url: "https://jasonbrass.ca",
            },
        ],
        media: [],
        lastUpdated: LAST_UPDATED,
    },
    {
        slug: "abdulkareem-akbik",
        name: "Abdulkareem Akbik",
        municipality: "milton",
        office: "Mayor of Milton",
        detailed: true,
        featured: false,
        series: null,
        editorialTitle: null,
        lede: "Abdulkareem Akbik is a candidate for Mayor of Milton in the 2026 municipal election.",
        achievements: [],
        sections: [],
        timeline: [],
        priorities: null,
        sources: [],
        media: [],
        lastUpdated: LAST_UPDATED,
    },
    {
        slug: "chez-amun",
        name: "Chéz Amun",
        municipality: "milton",
        office: "Mayor of Milton",
        detailed: true,
        featured: false,
        series: null,
        editorialTitle: null,
        lede: "Chéz Amun is a candidate for Mayor of Milton in the 2026 municipal election.",
        achievements: [],
        sections: [],
        timeline: [],
        priorities: null,
        sources: [],
        media: [],
        lastUpdated: LAST_UPDATED,
    },
    {
        slug: "arjun-joshi",
        name: "Arjun Joshi",
        municipality: "milton",
        office: "Mayor of Milton",
        detailed: true,
        featured: false,
        series: null,
        editorialTitle: null,
        lede: "Arjun Joshi is a candidate for Mayor of Milton in the 2026 municipal election.",
        achievements: [],
        sections: [],
        timeline: [],
        priorities: null,
        sources: [],
        media: [],
        lastUpdated: LAST_UPDATED,
    },
    {
        slug: "asif-khan",
        name: "Asif Khan",
        municipality: "milton",
        office: "Mayor of Milton",
        detailed: true,
        featured: false,
        series: null,
        editorialTitle: null,
        lede: "Asif Khan is a candidate for Mayor of Milton in the 2026 municipal election.",
        achievements: [],
        sections: [],
        timeline: [],
        priorities: null,
        sources: [],
        media: [],
        lastUpdated: LAST_UPDATED,
    },
    {
        slug: "rob-burton",
        name: "Rob Burton",
        municipality: "oakville",
        office: "Mayor of Oakville",
        detailed: true,
        featured: false,
        series: null,
        editorialTitle: null,
        lede: "Rob Burton has served as Oakville's mayor since 2006 and is seeking a sixth term in the 2026 municipal election.",
        achievements: [],
        sections: [
            {
                heading: "Municipal leadership",
                paragraphs: [
                    "His campaign draws on two decades of municipal leadership and experience overseeing Oakville's development and public services, emphasizing experience, continuity and results as Oakville plans its next chapter.",
                ],
            },
        ],
        timeline: [
            {
                period: "2006",
                title: "Mayor of Oakville",
                text: "Became Oakville's mayor in 2006 and has served in the office since.",
            },
            {
                period: "2026",
                title: "Seeking a sixth term",
                text: "Contesting the Oakville mayoral election on October 26, 2026.",
            },
        ],
        priorities: {
            note: PRIORITIES_NOTE,
            items: [
                {
                    title: "Managing growth responsibly",
                    text: "His published priorities include managing growth responsibly.",
                },
                {
                    title: "Neighbourhoods and green spaces",
                    text: "Protecting established neighbourhoods and green spaces.",
                },
                {
                    title: "Strong municipal finances",
                    text: "Maintaining strong municipal finances.",
                },
                {
                    title: "Community facilities",
                    text: "Investing in community facilities.",
                },
            ],
        },
        sources: [
            {
                title: "Official campaign website — robburton.ca",
                url: "https://www.robburton.ca/",
            },
        ],
        media: [],
        lastUpdated: LAST_UPDATED,
    },
    {
        slug: "arvind-choudhary",
        name: "Arvind Choudhary",
        municipality: "oakville",
        office: "Mayor of Oakville",
        detailed: true,
        featured: false,
        series: null,
        editorialTitle: null,
        lede: "Arvind Choudhary brings more than 30 years of experience in finance, public policy and technology to his candidacy for Mayor of Oakville in the 2026 municipal election.",
        achievements: [],
        sections: [],
        timeline: [],
        priorities: {
            note: PRIORITIES_NOTE,
            items: [
                {
                    title: "Preserving small-town character",
                    text: "Emphasizes preserving Oakville's small-town character.",
                },
                {
                    title: "Public safety and traffic infrastructure",
                    text: "Focuses on improving public safety and traffic infrastructure.",
                },
                {
                    title: "Responsible use of taxpayers' money",
                    text: "Commits to ensuring responsible use of taxpayers' money.",
                },
            ],
        },
        sources: [],
        media: [],
        lastUpdated: LAST_UPDATED,
    },
    {
        slug: "jeff-knoll",
        name: "Jeff Knoll",
        municipality: "oakville",
        office: "Mayor of Oakville",
        detailed: true,
        featured: false,
        series: null,
        editorialTitle: null,
        lede: "Jeff Knoll has represented Oakville's Ward 5 since 2000 and is a candidate for Mayor of Oakville in the 2026 municipal election.",
        achievements: [],
        sections: [
            {
                heading: "Boards and municipal experience",
                paragraphs: [
                    "Knoll has chaired the Halton Police Services Board and the Oakville Public Library Board. His municipal experience includes work on growth, taxation, roads and policing, which forms part of his case for becoming mayor.",
                ],
            },
        ],
        timeline: [
            {
                period: "2000",
                title: "Ward 5 councillor",
                text: "Began representing Oakville's Ward 5 in 2000.",
            },
            {
                period: "2026",
                title: "Candidate for Mayor of Oakville",
                text: "Contesting the Oakville mayoral election on October 26, 2026.",
            },
        ],
        priorities: null,
        sources: [],
        media: [],
        lastUpdated: LAST_UPDATED,
    },
    {
        slug: "sean-omeara",
        name: "Sean O'Meara",
        municipality: "oakville",
        office: "Mayor of Oakville",
        detailed: true,
        featured: false,
        series: null,
        editorialTitle: null,
        lede: "Sean O'Meara has represented Ward 1 as both town and regional councillor since 2014, and is a candidate for Mayor of Oakville in the 2026 municipal election.",
        achievements: [],
        sections: [
            {
                heading: "Background",
                paragraphs: [
                    "His background includes provincial politics, business leadership and community organizations.",
                ],
            },
        ],
        timeline: [
            {
                period: "2014",
                title: "Ward 1 town and regional councillor",
                text: "Began representing Ward 1 as both town and regional councillor in 2014.",
            },
            {
                period: "2026",
                title: "Candidate for Mayor of Oakville",
                text: "Contesting the Oakville mayoral election on October 26, 2026.",
            },
        ],
        priorities: {
            note: PRIORITIES_NOTE,
            items: [
                {
                    title: "Infrastructure and transportation",
                    text: "Emphasizes infrastructure and transportation improvements.",
                },
                {
                    title: "Economic development",
                    text: "Supports economic development in Oakville.",
                },
                {
                    title: "Shoreline protection and character",
                    text: "Supports shoreline protection and preserving Oakville's character amid growth.",
                },
            ],
        },
        sources: [],
        media: [],
        lastUpdated: LAST_UPDATED,
    },
    {
        slug: "keith-demoe",
        name: "Keith Demoe",
        municipality: "burlington",
        office: "Mayor of Burlington",
        detailed: true,
        featured: false,
        series: null,
        editorialTitle: null,
        lede: "Keith Demoe is a director of sales at a technology company and a candidate for Mayor of Burlington in the 2026 municipal election.",
        achievements: [],
        sections: [
            {
                heading: "Campaign focus",
                paragraphs: [
                    "His campaign focuses on improving the city and addressing residents' municipal concerns.",
                ],
            },
        ],
        timeline: [
            {
                period: "2022",
                title: "Ward 2 council candidate",
                text: "Contested the 2022 municipal election for Ward 2 councillor.",
            },
            {
                period: "2026",
                title: "Candidate for Mayor of Burlington",
                text: "Contesting the Burlington mayoral election on October 26, 2026.",
            },
        ],
        priorities: null,
        sources: [],
        media: [],
        lastUpdated: LAST_UPDATED,
    },
    {
        slug: "lisa-kearns",
        name: "Lisa Kearns",
        municipality: "burlington",
        office: "Mayor of Burlington",
        detailed: true,
        featured: false,
        series: null,
        editorialTitle: null,
        lede: "Lisa Kearns has represented Burlington's Ward 2 since 2018 and has served as Deputy Mayor of Engagement and Partnership since 2022. She is a candidate for Mayor of Burlington in the 2026 municipal election.",
        achievements: [],
        sections: [],
        timeline: [
            {
                period: "2018",
                title: "Ward 2 councillor",
                text: "Began representing Burlington's Ward 2 in 2018.",
            },
            {
                period: "2022",
                title: "Deputy Mayor of Engagement and Partnership",
                text: "Has served as Deputy Mayor of Engagement and Partnership since 2022.",
            },
            {
                period: "2026",
                title: "Candidate for Mayor of Burlington",
                text: "Contesting the Burlington mayoral election on October 26, 2026.",
            },
        ],
        priorities: {
            note: PRIORITIES_NOTE,
            items: [
                {
                    title: "Value for taxpayers",
                    text: "Focuses on delivering value for taxpayers.",
                },
                {
                    title: "Housing and climate resilience",
                    text: "Emphasizes housing and climate resilience.",
                },
                {
                    title: "Local businesses and evolving needs",
                    text: "Supports local businesses and addressing Burlington's evolving municipal needs.",
                },
            ],
        },
        sources: [],
        media: [],
        lastUpdated: LAST_UPDATED,
    },
    {
        slug: "marianne-meed-ward",
        name: "Marianne Meed Ward",
        municipality: "burlington",
        office: "Mayor of Burlington",
        detailed: true,
        featured: false,
        series: null,
        editorialTitle: null,
        lede: "Marianne Meed Ward was first elected mayor of Burlington in 2018, after serving as a councillor and community advocate, and is seeking a third consecutive term in the 2026 municipal election.",
        achievements: [],
        sections: [],
        timeline: [
            {
                period: "2018",
                title: "Mayor of Burlington",
                text: "First elected mayor of Burlington in 2018, after serving as a councillor and community advocate.",
            },
            {
                period: "2026",
                title: "Seeking a third term",
                text: "Contesting the Burlington mayoral election on October 26, 2026.",
            },
        ],
        priorities: {
            note: PRIORITIES_NOTE,
            items: [
                {
                    title: "Infrastructure investment",
                    text: "Emphasizes infrastructure investment.",
                },
                {
                    title: "Public safety and traffic",
                    text: "Focuses on public safety, traffic and transit improvements and reducing traffic congestion.",
                },
                {
                    title: "Residents' voices",
                    text: "Commits to resident engagement and ensuring residents' voices are heard in municipal decision-making and at other levels of government.",
                },
                {
                    title: "Affordability and costed budgets",
                    text: "Her published platform proposes costed budget options, affordability measures and improved congestion management.",
                },
                {
                    title: "Waterfront and green spaces",
                    text: "Protecting Burlington's waterfront and green spaces through responsible development.",
                },
            ],
        },
        sources: [
            {
                title: "Official campaign website — votemarianne.ca",
                url: "https://votemarianne.ca/",
            },
        ],
        media: [],
        lastUpdated: LAST_UPDATED,
    },
    {
        slug: "yugalkeesor-ramdhonee",
        name: "Yugalkeesor Ramdhonee",
        municipality: "burlington",
        office: "Mayor of Burlington",
        detailed: true,
        featured: false,
        series: null,
        editorialTitle: null,
        lede: "Yugalkeesor Ramdhonee is a first-time political candidate with a background in hospitality and a diploma in business administration from the United Kingdom. He is a candidate for Mayor of Burlington in the 2026 municipal election.",
        achievements: [],
        sections: [],
        timeline: [],
        priorities: {
            note: PRIORITIES_NOTE,
            items: [
                {
                    title: "Senior services",
                    text: "Highlights senior services.",
                },
                {
                    title: "Safer roads, sidewalks and transportation",
                    text: "Supports safer roads and sidewalks and improvements to transportation.",
                },
                {
                    title: "Affordable housing and community",
                    text: "Highlights affordable housing, community diversity, environmental concerns and improvements to municipal services.",
                },
            ],
        },
        sources: [],
        media: [],
        lastUpdated: LAST_UPDATED,
    },
    {
        slug: "ann-lawlor",
        name: "Ann Lawlor",
        municipality: "halton-hills",
        office: "Mayor of Halton Hills",
        detailed: true,
        featured: false,
        series: null,
        editorialTitle: null,
        lede: "Ann Lawlor has served as mayor of Halton Hills since 2022, following three terms as a Ward 4 councillor beginning in 2010. She is seeking re-election in the 2026 municipal election.",
        achievements: [],
        sections: [
            {
                heading: "Professional background",
                paragraphs: [
                    "Her professional background includes work in a family-owned marketing company, leadership at a social-service agency and consulting. She holds a master's degree in public administration from the London School of Economics.",
                ],
            },
        ],
        timeline: [
            {
                period: "2010",
                title: "Ward 4 councillor",
                text: "Began serving as a Ward 4 councillor in 2010, serving three terms.",
            },
            {
                period: "2022",
                title: "Mayor of Halton Hills",
                text: "Became mayor of Halton Hills in 2022.",
            },
            {
                period: "2026",
                title: "Seeking re-election",
                text: "Contesting the Halton Hills mayoral election on October 26, 2026.",
            },
        ],
        priorities: {
            note: PRIORITIES_NOTE,
            items: [
                {
                    title: "Responsible growth",
                    text: "Emphasizes responsible growth.",
                },
                {
                    title: "Fiscal accountability",
                    text: "Commits to fiscal accountability and respecting taxpayers' money.",
                },
                {
                    title: "Small-town character",
                    text: "Focuses on protecting Halton Hills' small-town character.",
                },
            ],
        },
        sources: [],
        media: [],
        lastUpdated: LAST_UPDATED,
    },
    {
        slug: "ivan-bosnjak",
        name: "Ivan Bosnjak",
        municipality: "halton-hills",
        office: "Mayor of Halton Hills",
        detailed: true,
        featured: false,
        series: null,
        editorialTitle: null,
        lede: "Ivan Bosnjak has lived in Halton Hills since 1989 and has a background in construction, small-business ownership and provincial labour relations. He is a candidate for Mayor of Halton Hills in the 2026 municipal election.",
        achievements: [],
        sections: [
            {
                heading: "Professional background",
                paragraphs: [
                    "Bosnjak spent 13 years in labour relations, negotiating province-wide collective bargaining agreements, and has worked in construction management.",
                ],
            },
        ],
        timeline: [],
        priorities: {
            note: PRIORITIES_NOTE,
            items: [
                {
                    title: "Sustainable growth, housing and mobility",
                    text: "Focuses on sustainable growth, housing, infrastructure and mobility.",
                },
                {
                    title: "Responsible budgeting",
                    text: "Commits to responsible budgeting and ensuring municipal government delivers value for residents.",
                },
                {
                    title: "Environmental priorities",
                    text: "Includes environmental priorities in his platform.",
                },
            ],
        },
        sources: [],
        media: [],
        lastUpdated: LAST_UPDATED,
    },
];

export const identifiedCoverage = {};

export const INCUMBENTS = ["rob-burton", "marianne-meed-ward", "ann-lawlor"];

export const starCandidateSlugs = [
    "zainab-azim",
    "jason-brass",
    "julia-hanna",
    "rory-nisan",
];

export const getMunicipality = (slug) =>
    municipalities.find((m) => m.slug === slug);

export const getCandidate = (slug) => candidates.find((c) => c.slug === slug);

export const getCandidatesByMunicipality = (slug) =>
    candidates.filter((c) => c.municipality === slug);

export const getDetailedCandidatesByMunicipality = (slug) =>
    candidates.filter((c) => c.municipality === slug && c.detailed);

export const getStarCandidates = () =>
    starCandidateSlugs.map(getCandidate).filter(Boolean);
